import { Card, Typography, Button, Modal, Badge } from 'antd'
import { CarOutlined, CopyOutlined, PhoneOutlined, DollarOutlined, UndoOutlined, UploadOutlined, ExclamationCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import Colors from '../utils/colors'
import { removeUnderScoreAndCapitalize } from '../helpers/strings'
import { showToast } from '../utils/toast'
import Constants from '../utils/constants'
import { getDateFromDateString, getDateTimeFromDateString } from '../helpers/Date'
import { useSelector, useDispatch } from 'react-redux'
import { updateTax, uploadTax, getAllTaxes } from '../actions/orderActions'
import { TAX_CONSTANTS } from '../constants/taxConstants'
import React, { useState, useRef } from 'react'

const { Text } = Typography

const TaxCard = ({ tax, onRefresh }) => {
  const dispatch = useDispatch()
  const { loading: updateTaxLoading, success: isUpdated, error: updateError } = useSelector((state) => state.updateTax)
  const { loading: uploadLoading, uploaded: isUploaded, error: uploadError } = useSelector((state) => state.uploadTax)
  const [refundModalVisible, setRefundModalVisible] = useState(false)
  const [uploadingTaxId, setUploadingTaxId] = useState(null)
  const [refundingTaxId, setRefundingTaxId] = useState(null)
  const fileInputRef = useRef(null)

  const copyTextFallback = (text) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed' // Avoid scrolling to bottom
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
      document.execCommand('copy')
    } catch (err) { }
    document.body.removeChild(textArea)
  }

  const copyToClipboard = async (text, label) => {
    try {
      navigator.clipboard ? navigator.clipboard.writeText(text) : copyTextFallback(text)
      showToast(`${label} copied to clipboard`, 'success')
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const downloadTax = () => {
    if (tax.fileUrl) {
      window.open(tax.fileUrl, '_blank')
    } else {
      showToast('Tax file not available', 'error')
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('orderId', tax.orderId)

    setUploadingTaxId(tax._id)
    dispatch(uploadTax(formData))
  }

  const triggerFileUpload = () => {
    fileInputRef.current?.click()
  }

  // Handle upload success/error
  React.useEffect(() => {
    if (isUploaded && uploadingTaxId === tax._id) {
      showToast('Tax uploaded successfully!', 'success')
      setUploadingTaxId(null)
      onRefresh()
    }

    if (uploadError && uploadingTaxId === tax._id) {
      showToast(uploadError, 'error')
      setUploadingTaxId(null)
    }
  }, [isUploaded, uploadError, uploadingTaxId, tax._id])

  // Handle refund success/error
  React.useEffect(() => {
    if (isUpdated && refundingTaxId === tax._id) {
      showToast('Order refunded successfully!', 'success')
      setRefundModalVisible(false)
      setRefundingTaxId(null)
      dispatch({ type: TAX_CONSTANTS.UPDATE_TAX_RESET })
      onRefresh()
    }

    if (updateError && refundingTaxId === tax._id) {
      showToast(updateError, 'error')
      setRefundingTaxId(null)
      dispatch({ type: TAX_CONSTANTS.UPDATE_TAX_RESET })
    }
  }, [isUpdated, updateError, refundingTaxId, tax._id, onRefresh, dispatch])

  const getCategoryColors = (category) => {
    const colorMap = {
      [Constants.MODES.BORDER_TAX]: { bg: Colors.lightBlue, text: Colors.darkBlueText },
      [Constants.MODES.ROAD_TAX]: { bg: Colors.lightGreen, text: Colors.darkGreenText },
      [Constants.MODES.ALL_INDIA_PERMIT]: { bg: Colors.lightOrange, text: Colors.darkOrangeText },
      [Constants.MODES.ALL_INDIA_TAX]: { bg: Colors.lightPurple, text: Colors.darkPurpleText },
      [Constants.MODES.LOADING_VEHICLE]: { bg: Colors.lightPink, text: Colors.darkPinkText }
    }
    return colorMap[category] || { bg: Colors.lightGray, text: Colors.primary }
  }

  const getTaxModeColors = (taxMode) => {
    const colorMap = {
      [Constants.TAX_MODES.DAYS]: { bg: Colors.lightRed, text: Colors.darkRedText },
      [Constants.TAX_MODES.WEEKLY]: { bg: Colors.lightCyan, text: Colors.darkCyanText },
      [Constants.TAX_MODES.MONTHLY]: { bg: Colors.lightBlue, text: Colors.darkBlueText },
      [Constants.TAX_MODES.QUARTERLY]: { bg: Colors.lightOrange, text: Colors.darkOrangeText },
      [Constants.TAX_MODES.YEARLY]: { bg: Colors.lightYellow, text: Colors.darkYellowText }
    }
    return colorMap[taxMode] || { bg: Colors.lightGray, text: Colors.gray }
  }

  const getStatusColor = (status) => {
    const colorMap = {
      [Constants.ORDER_STATUS.CONFIRMED]: 'processing',
      [Constants.ORDER_STATUS.CLOSED]: 'success',
      [Constants.ORDER_STATUS.CANCELLED]: 'error'
    }
    return colorMap[status] || 'default'
  }

  return (
    <Card
      hoverable
      style={{
        marginBottom: '20px',
        border: `1px solid ${Colors.darkerGray}`,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        minHeight: '280px',
        width: '100%',
        maxWidth: '400px'
      }}
      bodyStyle={{ padding: '20px' }}
    >
      {/* Header Section */}
      <div style={{
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: `1px solid ${Colors.lightGray}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '8px'
        }}>
          <Text strong style={{ fontSize: '18px', color: Colors.primary }}>
            {(!tax.state && !tax.border) ? 'N/A' : `${removeUnderScoreAndCapitalize(tax.state || '')}${tax.border ? ` - ${removeUnderScoreAndCapitalize(tax.border)}` : ''}`}
          </Text>
          <div style={{
            backgroundColor: getCategoryColors(tax.category).bg,
            padding: '4px 8px',
            borderRadius: '12px',
            border: `1px solid ${getCategoryColors(tax.category).text}20`
          }}>
            <Text style={{ fontSize: '13px', color: getCategoryColors(tax.category).text, fontWeight: 'bold' }}>
              {removeUnderScoreAndCapitalize(tax.category)}
            </Text>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Badge
            status={getStatusColor(tax.status)}
            text={removeUnderScoreAndCapitalize(tax.status)}
            style={{ fontSize: '13px' }}
          />
          <Text style={{ fontSize: '13px', color: Colors.darkGray }}>
            {getDateTimeFromDateString(tax.createdAt)}
          </Text>
        </div>
      </div>

      {/* Main Info */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CarOutlined style={{ color: Colors.primary, marginRight: '8px', fontSize: '16px' }} />
            <Text style={{ fontSize: '14px' }}>Vehicle Number</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text style={{ fontSize: '14px', marginRight: '8px' }}>{tax.vehicleNumber}</Text>
            <CopyOutlined
              style={{ cursor: 'pointer', color: Colors.primary, fontSize: '14px' }}
              onClick={() => copyToClipboard(tax.vehicleNumber, 'Vehicle Number')}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PhoneOutlined style={{ color: Colors.primary, marginRight: '8px', fontSize: '16px' }} />
            <Text style={{ fontSize: '14px' }}>Mobile Number</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text style={{ fontSize: '14px', marginRight: '8px' }}>{tax.mobileNumber}</Text>
            <CopyOutlined
              style={{ cursor: 'pointer', color: Colors.primary, fontSize: '14px' }}
              onClick={() => copyToClipboard(tax.mobileNumber, 'Mobile Number')}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DollarOutlined style={{ color: Colors.primary, marginRight: '8px', fontSize: '16px' }} />
            <Text style={{ fontSize: '14px' }}>Amount</Text>
          </div>
          <Text strong style={{ fontSize: '17px', color: Colors.primary, fontWeight: 'bold' }}>
            ₹{tax.amount}
          </Text>
        </div>
      </div>

      {/* Additional Info */}
      <div style={{ fontSize: '14px', color: Colors.darkGray, lineHeight: '1.6' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <Text style={{ fontSize: '14px' }}>Seat Capacity</Text>
          <Text style={{ fontSize: '14px' }}>{tax.seatCapacity}</Text>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Text style={{ fontSize: '14px' }}>Tax Mode</Text>
          <div style={{
            backgroundColor: getTaxModeColors(tax.taxMode).bg,
            padding: '2px 6px',
            borderRadius: '8px',
            border: `1px solid ${getTaxModeColors(tax.taxMode).text}20`
          }}>
            <Text style={{ fontSize: '12px', color: getTaxModeColors(tax.taxMode).text, fontWeight: 'bold' }}>
              {removeUnderScoreAndCapitalize(tax.taxMode)}
            </Text>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <Text style={{ fontSize: '14px' }}>Tax Period</Text>
          <Text style={{ fontSize: '14px' }}>{getDateFromDateString(tax.startDate)} - {getDateFromDateString(tax.endDate)}</Text>
        </div>

        {tax.chasisNumber && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <Text style={{ fontSize: '14px' }}>Chasis Number</Text>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text style={{ fontSize: '14px', marginRight: '8px' }}>{tax.chasisNumber}</Text>
              <CopyOutlined
                style={{ cursor: 'pointer', color: Colors.primary, fontSize: '13px' }}
                onClick={() => copyToClipboard(tax.chasisNumber, 'Chasis Number')}
              />
            </div>
          </div>
        )}

        {tax.status === Constants.ORDER_STATUS.CLOSED && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Text style={{ fontSize: '14px' }}>Completed By</Text>
            <Text style={{ fontSize: '14px' }}>{tax.whoCompleted?.username || 'N/A'}</Text>
          </div>
        )}
      </div>

      {/* Action Buttons - Show different buttons based on status */}
      {tax.status === Constants.ORDER_STATUS.CONFIRMED && (
        <div style={{
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${Colors.lightGray}`,
          display: 'flex',
          justifyContent: 'space-between',
          gap: '8px'
        }}>
          <Button
            size="small"
            icon={<UndoOutlined />}
            style={{
              fontSize: '12px',
              height: '28px',
              backgroundColor: Colors.white,
              borderColor: Colors.red,
              color: Colors.red,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              const button = e.currentTarget
              button.style.backgroundColor = Colors.red
              button.style.color = Colors.white
            }}
            onMouseLeave={(e) => {
              const button = e.currentTarget
              button.style.backgroundColor = Colors.white
              button.style.color = Colors.red
            }}
            onClick={() => setRefundModalVisible(true)}
          >
            Refund
          </Button>

          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
            />
            <Button
              size="small"
              icon={<UploadOutlined />}
              loading={uploadingTaxId === tax._id && uploadLoading}
              style={{
                fontSize: '12px',
                height: '28px'
              }}
              onClick={triggerFileUpload}
            >
              Upload Tax
            </Button>
          </>
        </div>
      )}

      {tax.status === Constants.ORDER_STATUS.CLOSED && (
        <div style={{
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${Colors.lightGray}`,
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            style={{
              fontSize: '12px',
              height: '28px'
            }}
            onClick={downloadTax}
          >
            Download Tax
          </Button>
        </div>
      )}

      {/* Refund Confirmation Modal */}
      <Modal
        title="Confirm Refund"
        open={refundModalVisible}
        onOk={() => {
          setRefundingTaxId(tax._id)
          dispatch(updateTax(tax._id, { status: Constants.ORDER_STATUS.CANCELLED }))
        }}
        onCancel={() => {
          setRefundModalVisible(false)
          setRefundingTaxId(null)
        }}
        okText="Yes, Refund"
        cancelText="Cancel"
        okType="danger"
        centered
        confirmLoading={refundingTaxId === tax._id && updateTaxLoading}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExclamationCircleOutlined style={{ color: Colors.warning, fontSize: '22px', marginRight: 12 }} />
          <span>Are you sure you want to refund this order? This action cannot be undone.</span>
        </div>
      </Modal>
    </Card>
  )
}

export default TaxCard